import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { EMPTY_FILTERS, filterOptionsQuery, tablePageQuery, type Filters, type ListName } from '../api/opteemum'
import type { Named } from '../api/shell'
import { Content } from '../ui/Shell'
import { DataTable, useTableState } from '../ui/DataTable'
import { DeviceWorkspace } from '../devices/DeviceWorkspace'
import { MultiDevicesWidget } from './MultiRemoteActions'
import { deviceColumns, serverColumns, type DeviceRow } from './columns'
import { openRemoteConnect, remoteConnectTargetOf } from '../remote_connect/RemoteConnectLaunch'

/**
 * Listele Opteemum - Workstations, Servers, Mobiles, Inactive, Live Devices -
 * cu machetele vechi: aceleasi id-uri de tabel, aceleasi coloane, aceleasi
 * filtre (groups + intervalele din sidebar).
 */

export const TABLE_ID: Record<ListName, string> = {
  workstations: 'opteemumWorkstations',
  servers: 'opteemumServers',
  mobiles: 'opteemumMobiles',
  inactive: 'opteemumInactiveDevices',
  live_devices: 'opteemumDevices',
}

function Select({ label, name, options, value, onChange }: { label: string; name: string; options: Named[]; value: string; onChange(v: string): void }) {
  return (
    <div className="col-md-4">
      <label>{label}</label>
      <select className="form-control input-sm" name={name} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select...</option>
        {options.map((o) => (
          <option key={o.id} value={String(o.id)}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  )
}

function Range({ label, name, value, onChange }: { label: string; name: string; value: string; onChange(v: string): void }) {
  return (
    <div className="col-md-4">
      <label>{label}</label>
      <input className="form-control input-sm" name={name} value={value} onChange={(e) => onChange(e.target.value)} placeholder="min;max" />
    </div>
  )
}

function FiltersPanel({ applied, onApply, close }: { applied: Filters; onApply(f: Filters): void; close(): void }) {
  const options = useQuery(filterOptionsQuery)
  const [draft, setDraft] = useState<Filters>(applied)
  const set = (key: keyof Filters) => (v: string) => setDraft((d) => ({ ...d, [key]: v }))
  const groups = draft.groups[0] ?? ''

  return (
    <>
      <header className="panel-heading sm bg-default">
        <h4>
          <i className="fa fa-filter"></i> <strong>Apply</strong> filters
        </h4>
      </header>
      <div className="panel-body">
        <form
          className="form-horizontal labelcustomize tableFilters"
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            onApply(draft)
          }}
          onReset={(e) => {
            e.preventDefault()
            setDraft(EMPTY_FILTERS)
          }}
        >
          <div className="form-group">
            <Select label="Groups" name="groups[]" options={options.data?.groups ?? []} value={groups} onChange={(v) => setDraft((d) => ({ ...d, groups: v ? [v] : [] }))} />
            <Range label="Workstation Efficiency Index" name="wei" value={draft.wei} onChange={set('wei')} />
            <Range label="Available updates" name="available_updates" value={draft.available_updates} onChange={set('available_updates')} />
          </div>
          <div className="form-group">
            <Range label="Installed applications" name="installed_applications" value={draft.installed_applications} onChange={set('installed_applications')} />
            <Range label="Startup applications" name="startup_applications" value={draft.startup_applications} onChange={set('startup_applications')} />
            <Range label="Event Viewer errors" name="event_viewer_errors" value={draft.event_viewer_errors} onChange={set('event_viewer_errors')} />
          </div>
          <div className="form-group offset">
            <div className="col-md-offset-5 col-md-7">
              <button type="submit" className="btn btn-theme applyFilters">
                Apply
              </button>{' '}
              <button type="reset" className="btn btn-inverse">
                Reset
              </button>{' '}
              <button type="button" className="btn closeFilters" onClick={close}>
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export function DeviceListPage({ list }: { list: ListName }) {
  const id = TABLE_ID[list]
  /*
   * Ordonarea de pornire, ca in `OpteemumDevices.initDatatable`: pe servere
   * dupa numele masinii, pe restul dupa grup. Nu e acelasi numar de coloana
   * fiindca nu e acelasi tabel - a cincea de pe servere e Health, si dupa ea
   * nu se poate ordona; a cere-o ar fi trimis serverului un nume de coloana
   * pe care el nu-l stie.
   */
  const start = list === 'servers' ? { column: 2, dir: 'asc' as const } : { column: 5, dir: 'desc' as const }
  const [state, setState] = useTableState(id, { start: 0, length: 25, order: start, search: '' })
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [widgetHidden, setWidgetHidden] = useState(false)
  const columns = useMemo(() => (list === 'servers' ? serverColumns() : deviceColumns()), [list])
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[2]
  const query = useQuery({
    ...tablePageQuery({
      list,
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
      filters,
    }),
    placeholderData: keepPreviousData,
  })
  const rows = (query.data?.data ?? []) as DeviceRow[]
  const activeFilters = Object.entries(filters).filter(([k, v]) => (k === 'groups' ? (v as string[]).length > 0 : v !== '')).length

  const onTableClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    // Butonul verde "Remote connect" din coloana Actions vine ca HTML de la
    // server (data-target="#remoteConnectModal"); fara asta clicul nu facea nimic.
    const remote = remoteConnectTargetOf(e.target)
    if (remote) {
      e.preventDefault()
      openRemoteConnect(remote)
      return
    }
    if (target instanceof HTMLInputElement && target.classList.contains('oneDevice')) {
      const idNum = Number(target.value)
      setWidgetHidden(false)
      setSelected((s) => {
        const next = new Set(s)
        if (target.checked) next.add(idNum)
        else next.delete(idNum)
        return next
      })
    } else if (target instanceof HTMLInputElement && target.classList.contains('allDevices')) {
      setWidgetHidden(false)
      setSelected(target.checked ? new Set(rows.map((r) => r.deviceId)) : new Set())
    }
  }

  return (
    <Content>
      <div onClick={onTableClick}>
        <DataTable
          id={id}
          columnTools
          columns={columns}
          rows={rows.map((r) => ({ ...r, __checked: selected.has(r.deviceId) }))}
          total={query.data?.recordsTotal ?? 0}
          fetching={query.isFetching}
          error={query.isError ? (query.error as Error).message : null}
          onRetry={() => void query.refetch()}
          state={state}
          onState={setState}
          rowId={(r) => String(r.DT_RowId ?? `row_${r.deviceId}`)}
          details={(row) => <DeviceWorkspace id={Number(row.deviceId)} from="opteemum" />}
          filters={{
            active: activeFilters,
            render: (close) => (
              <FiltersPanel
                applied={filters}
                close={close}
                onApply={(f) => {
                  setFilters(f)
                  setState({ start: 0 })
                }}
              />
            ),
          }}
          footerButtons={{ copy: true, colvis: true, refresh: () => void query.refetch() }}
        >
          <MultiDevicesWidget
            shown={selected.size > 0 && !widgetHidden}
            ids={[...selected]}
            onHide={() => setWidgetHidden(true)}
          />
        </DataTable>
      </div>
    </Content>
  )
}
